import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { loadRuntimeConfig } from "../config";
import { buildAuthorizeUrl, buildLogoutUrl, exchangeCodeForTokens, AuthError } from "./authClient";
import { readCognitoRole, type CognitoRole } from "./jwt";
import { generateCodeChallenge, generateCodeVerifier, generateState } from "./pkce";
import {
  clearPendingAuthorization,
  clearTokens,
  isTokenValid,
  readAndClearFlash,
  readPendingAuthorization,
  readTokens,
  writeFlash,
  writePendingAuthorization,
  writeTokens,
  type StoredTokens,
} from "./session";
import type { RuntimeConfig } from "../../types";

type AuthStatus = "loading" | "unauthenticated" | "authenticated" | "config-error";

interface AuthContextValue {
  status: AuthStatus;
  config: RuntimeConfig | null;
  email: string | null;
  role: CognitoRole;
  accessToken: string | null;
  configError: string | null;
  authError: string | null;
  flash: string | null;
  signIn: (options?: { screenHint?: "signup" }) => Promise<void>;
  signOut: () => void;
  dismissAuthError: () => void;
  /** Call after any owner API 401/403 to force a fresh sign-in with a clear reason. */
  invalidateSession: (reason: string) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);


export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [config, setConfig] = useState<RuntimeConfig | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [tokens, setTokens] = useState<StoredTokens | null>(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    void (async () => {
      let loadedConfig: RuntimeConfig;
      try {
        loadedConfig = await loadRuntimeConfig();
      } catch (err) {
        setConfigError(err instanceof Error ? err.message : "Configuration failed to load.");
        setStatus("config-error");
        return;
      }
      setConfig(loadedConfig);

      const url = new URL(window.location.href);
      const code = url.searchParams.get("code");
      const state = url.searchParams.get("state");
      const oauthError = url.searchParams.get("error");
      const isCallback = url.pathname === "/owner" && (code || oauthError);

      if (isCallback) {
        window.history.replaceState({}, "", url.pathname);

        if (oauthError) {
          setAuthError(
            url.searchParams.get("error_description") ?? "Sign-in was cancelled. You can try again below.",
          );
          clearPendingAuthorization();
          setStatus("unauthenticated");
          return;
        }

        const pending = readPendingAuthorization();
        clearPendingAuthorization();
        if (!pending || pending.state !== state || !code) {
          setAuthError("This sign-in link is no longer valid. Please sign in again.");
          setStatus("unauthenticated");
          return;
        }

        try {
          const nextTokens = await exchangeCodeForTokens(loadedConfig, { code, verifier: pending.verifier });
          writeTokens(nextTokens);
          setTokens(nextTokens);
          setStatus("authenticated");
        } catch (err) {
          setAuthError(err instanceof AuthError ? err.message : "Sign-in didn't complete. Please try again.");
          setStatus("unauthenticated");
        }
        return;
      }

      const stored = readTokens();
      const message = readAndClearFlash();
      if (message) setFlash(message);

      if (isTokenValid(stored)) {
        setTokens(stored);
        setStatus("authenticated");
      } else {
        if (stored) clearTokens();
        setStatus("unauthenticated");
      }
    })();
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    async function signIn(options?: { screenHint?: "signup" }) {
      if (!config) return;
      const verifier = generateCodeVerifier();
      const challenge = await generateCodeChallenge(verifier);
      const state = generateState();
      writePendingAuthorization(verifier, state, window.location.pathname);
      window.location.assign(
        buildAuthorizeUrl(config, { state, codeChallenge: challenge, screenHint: options?.screenHint }),
      );
    }

    function signOut() {
      clearTokens();
      setTokens(null);
      setStatus("unauthenticated");
      if (config) window.location.assign(buildLogoutUrl(config));
    }

    function invalidateSession(reason: string) {
      clearTokens();
      setTokens(null);
      writeFlash(reason);
      setFlash(reason);
      setStatus("unauthenticated");
    }

    return {
      status,
      config,
      email: tokens?.email ?? null,
      role: readCognitoRole(isTokenValid(tokens) ? tokens.accessToken : null),
      accessToken: isTokenValid(tokens) ? tokens.accessToken : null,
      configError,
      authError,
      flash,
      signIn,
      signOut,
      dismissAuthError: () => setAuthError(null),
      invalidateSession,
    };
  }, [status, config, configError, authError, flash, tokens]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
